import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import useMobile from "../../hooks/useMobile";
import { ICooperative } from "../../exports/interfaces";
import NepaliDate from "nepali-date-converter";

interface LineChartProps {
  cooperative: ICooperative[];
}

const LineChart = ({ cooperative }: LineChartProps) => {
  const isMobile = useMobile();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [hoveredDot, setHoveredDot] = useState<{
    value: number;
    color: string;
    type: string;
    x: number;
    y: number;
  } | null>(null);

  const height = 300;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width;
      setWidth(newWidth);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const data = React.useMemo(() => {
    const yearMap: Record<
      string,
      {
        year: string;
        a: number;
        b: number;
      }
    > = {};

    cooperative.forEach((item) => {
      const date = new Date(item.createdAt);

      if (isNaN(date.getTime())) return;

      const nepaliDate = new NepaliDate(date);
      const year = nepaliDate.getYear().toString();

      if (!yearMap[year]) {
        yearMap[year] = {
          year,
          a: 0,
          b: 0,
        };
      }

      // b = all cooperatives
      yearMap[year].a += 1;

      // a = Active cooperatives
      if (item.status === "Active") {
        yearMap[year].b += 1;
      }
    });

    return Object.values(yearMap).sort(
      (a, b) => Number(a.year) - Number(b.year),
    );
  }, [cooperative]);

  // D3 chart
  useEffect(() => {
    if (!width || !data.length) return;

    const margin = {
      top: 30,
      right: 30,
      bottom: 40,
      left: 40,
    };

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    svg.selectAll("*").remove();

    const defs = svg.append("defs");

    // Gradient for line A
    const gradientA = defs
      .append("linearGradient")
      .attr("id", "gradientA")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradientA
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "var(--primary-color)")
      .attr("stop-opacity", 0.25);

    gradientA
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "var(--primary-color)")
      .attr("stop-opacity", 0);

    // Gradient for line B
    const gradientB = defs
      .append("linearGradient")
      .attr("id", "gradientB")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradientB
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#2BA0DC")
      .attr("stop-opacity", 0.12);

    gradientB
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#2BA0DC")
      .attr("stop-opacity", 0);

    const x = d3
      .scalePoint()
      .domain(data.map((d) => d.year))
      .range([margin.left, width - margin.right]);

    const maxValue = d3.max(data, (d) => Math.max(d.a, d.b))!;

    const y = d3
      .scaleLinear()
      .domain([0, Math.ceil(maxValue / 10) * 10])
      .range([height - margin.bottom, margin.top]);

    // X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x))
      .call((g) =>
        g
          .selectAll("text")
          .attr("fill", "var(--text-main)")
          .style("font-size", "13px"),
      )
      .call((g) =>
        g.selectAll("path,line").attr("stroke", "var(--input-border)"),
      );

    // Y Axis
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5))
      .call((g) =>
        g
          .selectAll("text")
          .attr("fill", "var(--text-main)")
          .style("font-size", "13px"),
      )
      .call((g) =>
        g.selectAll("path,line").attr("stroke", "var(--input-border)"),
      );

    // Grid
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickSize(-width + margin.left + margin.right)
          .tickFormat(() => ""),
      )
      .call((g) =>
        g
          .selectAll("line")
          .attr("stroke", "var(--input-border)")
          .attr("opacity", 0.5),
      )
      .call((g) => g.select(".domain").remove());

    const lineA = d3
      .line<any>()
      .x((d) => x(d.year)!)
      .y((d) => y(d.a));

    const lineB = d3
      .line<any>()
      .x((d) => x(d.year)!)
      .y((d) => y(d.b));

    const areaA = d3
      .area<any>()
      .x((d) => x(d.year)!)
      .y0((d) => y(d.a))
      .y1(height - margin.bottom);

    const areaB = d3
      .area<any>()
      .x((d) => x(d.year)!)
      .y0((d) => y(d.b))
      .y1(height - margin.bottom);

    // Shadow area
    svg
      .append("path")
      .datum(data)
      .attr("d", areaA)
      .attr("fill", "url(#gradientA)");

    svg
      .append("path")
      .datum(data)
      .attr("d", areaB)
      .attr("fill", "url(#gradientB)");

    // Lines
    svg
      .append("path")
      .datum(data)
      .attr("d", lineA)
      .attr("fill", "none")
      .attr("stroke", "var(--primary-color)")
      .attr("stroke-width", 2);

    svg
      .append("path")
      .datum(data)
      .attr("d", lineB)
      .attr("fill", "none")
      .attr("stroke", "#2BA0DC")
      .attr("stroke-width", 2);

    // Dots A
    svg
      .selectAll(".dotA")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => x(d.year)!)
      .attr("cy", (d) => y(d.a))
      .attr("r", 4)
      .attr("fill", "var(--primary-color)")
      .style("cursor", "pointer")
      .on("mouseenter", (event: any, d: any) => {
        const [hoverX, hoverY] = d3.pointer(event, svgRef.current);
        setHoveredDot({
          value: d.a,
          color: "var(--primary-color)",
          type: "Cooperative Registration",
          x: hoverX,
          y: hoverY,
        });
      })
      .on("mouseleave", () => setHoveredDot(null));

    // Dots B
    svg
      .selectAll(".dotB")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => x(d.year)!)
      .attr("cy", (d) => y(d.b))
      .attr("r", 4)
      .attr("fill", "#2BA0DC")
      .style("cursor", "pointer")
      .on("mouseenter", (event: any, d: any) => {
        const [hoverX, hoverY] = d3.pointer(event, svgRef.current);
        setHoveredDot({
          value: d.b,
          color: "#2BA0DC",
          type: "Active Cooperatives",
          x: hoverX,
          y: hoverY,
        });
      })
      .on("mouseleave", () => setHoveredDot(null));
  }, [data, width]);

  return (
    <>
      <div ref={containerRef} style={{ width: "100%", position: "relative" }}>
        <svg ref={svgRef}></svg>
        {hoveredDot && (
          <div
            style={{
              position: "absolute",
              left: `${hoveredDot.x}px`,
              top: `${hoveredDot.y - 40}px`,
              backgroundColor: hoveredDot.color,
              color: "white",
              padding: "6px 12px",
              borderRadius: "var(--input-radius)",
              fontSize: "14px",
              whiteSpace: "nowrap",
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              transform: "translateX(-50%)",
              opacity: 0.7,
            }}
          >
            {hoveredDot.value}{" "}
            {hoveredDot.type === "Active Cooperatives"
              ? "Active"
              : "Registered"}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: isMobile ? "0px" : "20px",
          paddingLeft: "40px",
          flexDirection: isMobile ? "column" : "row",
          marginBottom: isMobile ? "10px" : "0px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "var(--primary-color)",
            }}
          ></div>
          <span style={{ color: "var(--primary-color)" }}>
            Cooperative Registration
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#2BA0DC",
            }}
          ></div>
          <span style={{ color: "#2BA0DC" }}>Active Cooperatives</span>
        </div>
      </div>
    </>
  );
};

export default LineChart;
